import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import MaskedInput from 'react-text-mask';
import isEmpty from 'lodash.isempty';
import cn from 'classnames';
import { endOfDay, isAfter, parse, startOfDay, subYears } from 'date-fns';

import { Button, Select } from '../../../components';
import { PHONE_MASK } from '../../../config/constants';
import { isMobilePhoneInvalid } from '../../../lib/util';

const TextaVisitModal = ({
	isOpen,
	onCancel,
	providers,
	onSubmit,
	patient_first_name = '',
	patient_last_name = '',
	patient_phone = '',
	patient_dob = '',
	currentUser = '',
	onVerifyMobileNumber,
	phoneData,
}) => {
	const [name_first, setNameFirst] = useState('');
	const [name_last, setNameLast] = useState('');
	const [email_address, setEmailAddress] = useState('');
	const [mobile_phone, setMobilePhone] = useState('');
	const [birth_dt, setBirthDate] = useState('');
	const [visit_provider_id, setVisitProviderId] = useState(currentUser);
	const [isGuardianRequired, setIsGuardianRequired] = useState(false);
	const [dependent_first, setDependentFirstName] = useState('');
	const [dependent_last, setDependentLastName] = useState('');
	const [isPhoneVerified, setPhoneVerified] = useState(false);

	const providerOptions = providers.map(({ id, ...provider }) => ({
		value: `${id}`,
		label: `${provider.name_first} ${provider.name_last}, ${provider.credentials}`,
	}));
	providerOptions.unshift({ value: '', label: 'Choose...' });

	useEffect(() => {
		setNameFirst(patient_first_name);
		setNameLast(patient_last_name);
		setEmailAddress('');
		setMobilePhone(patient_phone);
		setBirthDate(patient_dob);
		setVisitProviderId(currentUser);
		setIsGuardianRequired(false);
		setDependentFirstName('');
		setDependentLastName('');
		if (patient_phone.length > 0 && patient_phone.length === 10) {
			setMobilePhone(
				`(${patient_phone.substring(0, 3)}) ${patient_phone.substring(
					3,
					6
				)}-${patient_phone.substring(6)}`
			);
		}
		setPhoneVerified(false);
	}, [isOpen]);

	useEffect(() => {
		if (!isPhoneVerified) {
			if (isMobilePhoneInvalid(mobile_phone)) {
				setPhoneVerified(false);
			} else if (
				!isEmpty(mobile_phone) &&
				!isMobilePhoneInvalid(mobile_phone) &&
				!phoneData
			) {
				onVerifyMobileNumber(mobile_phone);
			} else if (
				!isEmpty(mobile_phone) &&
				!isMobilePhoneInvalid(mobile_phone) &&
				phoneData
			) {
				setPhoneVerified(true);
			}
		}
	}, [mobile_phone, phoneData, isPhoneVerified]);

	// validation
	const isValid =
		name_first &&
		name_first.length > 0 &&
		name_last &&
		name_last.length > 0 &&
		birth_dt &&
		birth_dt.length === 10 &&
		visit_provider_id &&
		visit_provider_id !== '' &&
		((email_address && email_address.length > 4) ||
			(!isEmpty(mobile_phone) &&
				mobile_phone.length === PHONE_MASK.length &&
				!isMobilePhoneInvalid(mobile_phone) &&
				phoneData &&
				phoneData.isTAVCapable)) &&
		((isGuardianRequired &&
			dependent_first &&
			dependent_first.length > 0 &&
			dependent_last &&
			dependent_last.length > 0) ||
			!isGuardianRequired);

	const verifyDOB = ({ target }) => {
		setBirthDate(target.value);
		const birthDt = startOfDay(parse(target.value, 'yyyy-MM-dd', new Date()));
		setIsGuardianRequired(isAfter(birthDt, endOfDay(subYears(new Date(), 18))));
	};

	const handleVerifyMobile = () => {
		setPhoneVerified(false);
		if (
			!isEmpty(mobile_phone) &&
			mobile_phone.length === PHONE_MASK.length &&
			!isMobilePhoneInvalid(mobile_phone)
		) {
			onVerifyMobileNumber(mobile_phone);
		}
	};

	// save/create
	const handleSubmission = () => {
		const submissionData = {
			birth_dt: parse(birth_dt, 'yyyy-MM-dd', new Date()).toISOString(),
			name_first,
			name_last,
			email_address,
			visit_provider_id,
			mobile_phone: isMobilePhoneInvalid(mobile_phone, true),
			dependent_first,
			dependent_last,
		};
		onSubmit(submissionData);
	};

	return (
		<Modal
			isOpen={isOpen}
			contentLabel="Create Text-a-Visit"
			className="modal-dialog text-a-visit-modal"
		>
			<div className="modal-content">
				<div className="modal-header">
					<div className="clearfix">
						<div className="pull-right">
							<button className="btn btn-link" onClick={onCancel}>
								<em className="fa fa-lg fa-times" />
							</button>
						</div>
						<h3 className="modal-title">Create Text-a-Visit</h3>
						<p>
							You can instantly invite a patient to start a visit from any
							compatible device without them needing a patient account or app!
							Simply provide an email and/or phone number for your patient. They
							will receive an invite link in their texts and/or email.
						</p>
					</div>
				</div>
				<div className="modal-body form">
					<div className="form-group">
						<label>Patient Date of Birth</label>
						<input
							type="date"
							className="form-control"
							value={birth_dt}
							onChange={verifyDOB}
						/>
					</div>
					{isGuardianRequired && (
						<React.Fragment>
							<div className="form-group">
								<label>Dependent (Patient) First Name</label>
								<input
									type="text"
									className="form-control"
									value={dependent_first}
									onChange={({ target }) => setDependentFirstName(target.value)}
								/>
							</div>
							<div className="form-group">
								<label>Dependent (Patient) Last Name</label>
								<input
									type="text"
									className="form-control"
									value={dependent_last}
									onChange={({ target }) => setDependentLastName(target.value)}
								/>
							</div>
						</React.Fragment>
					)}
					<div className="form-group">
						<label>
							{isGuardianRequired ? 'Guardian' : 'Patient'} First Name
						</label>
						<input
							type="text"
							className="form-control"
							value={name_first}
							onChange={({ target }) => setNameFirst(target.value)}
						/>
					</div>
					<div className="form-group">
						<label>
							{isGuardianRequired ? 'Guardian' : 'Patient'} Last Name
						</label>
						<input
							type="text"
							className="form-control"
							value={name_last}
							onChange={({ target }) => setNameLast(target.value)}
						/>
					</div>
					<div
						className={cn('form-group', {
							'has-error':
								isPhoneVerified && phoneData && !phoneData.isTAVCapable,
						})}
					>
						<label className="control-label">
							{isGuardianRequired ? 'Guardian' : 'Patient'} Mobile Phone
						</label>
						<MaskedInput
							className="form-control"
							mask={PHONE_MASK}
							value={mobile_phone}
							onChange={({ target }) => setMobilePhone(target.value)}
							onBlur={handleVerifyMobile}
						/>
						{isPhoneVerified && (
							<React.Fragment>
								{phoneData && phoneData.isTAVCapable && (
									<span className="help-block">
										Phone number is Text-a-Visit capable
									</span>
								)}
								{phoneData && !phoneData.isTAVCapable && !phoneData.err && (
									<span className="help-block">
										Phone number is not Text-a-Visit capable
									</span>
								)}
								{phoneData && phoneData.err && (
									<span className="help-block">
										Unable to verify phone number
									</span>
								)}
							</React.Fragment>
						)}
					</div>
					<div className="form-group">
						<label>
							{isGuardianRequired ? 'Guardian' : 'Patient'} Email Address
						</label>
						<input
							type="email"
							className="form-control"
							value={email_address}
							onChange={({ target }) => setEmailAddress(target.value)}
						/>
					</div>
					<div className="form-group">
						<label>Visit With Provider</label>
						<Select
							onChange={({ target }) => setVisitProviderId(target.value)}
							selectedValue={visit_provider_id}
							options={providerOptions}
						/>
					</div>
				</div>
				<div className="modal-footer">
					<div className="pull-right">
						<Button
							title="Cancel"
							large
							onClick={onCancel}
							disabled={false /*isSaving*/}
						/>
						<Button
							title="Create"
							onClick={handleSubmission}
							disabled={!isValid /*isEditting || isSaving*/}
							large
							primary
							className="ml"
						/>
					</div>
				</div>
			</div>
		</Modal>
	);
};

TextaVisitModal.propTypes = {
	isOpen: PropTypes.bool,
	onCancel: PropTypes.func.isRequired,
	providers: PropTypes.array.isRequired,
	onSubmit: PropTypes.func.isRequired,
	patient_first_name: PropTypes.string,
	patient_last_name: PropTypes.string,
	patient_dob: PropTypes.string,
	patient_phone: PropTypes.string,
	currentUser: PropTypes.string,
	onVerifyMobileNumber: PropTypes.func,
	phoneData: PropTypes.object,
};

export default TextaVisitModal;
